<!--
  src/components/DialogAddTask.vue
  Create / edit a task in the BANK (master catalog).
  Body maps 1:1 to CreateTasksBankModel:
    TaskTypeId (req), SubTypeId?, ExternalId?, SubjectId?, SubjectName,
    Name (req), Description, DurationMinutes, Stage, StageDay,
    Priority, MustDo, Tags, TaskComments
  Tags is displayed read-only and round-trips unchanged.
  Opened as a Buefy modal (hasModalCard), from the "new" button and on row-click.
-->
<template>
  <div class="DialogAddTask">
    <div class="modal-card" style="width: 620px;">
      <header class="modal-card-head">
        <p class="modal-card-title">
          {{ isEdit ? "עריכת משימה בבנק" : "משימה חדשה בבנק" }}
          <span v-if="isEdit" class="task-id">{{ row.id }}</span>
        </p>
        <button type="button" class="delete" @click="$emit('close')" />
      </header>

      <section class="modal-card-body">
        <!-- פרטים -->
        <h4 class="section-container">פרטי משימה</h4>
        <b-field label="שם המשימה *" :type="submitted && !model.name ? 'is-danger' : ''"
          :message="submitted && !model.name ? 'שדה חובה' : ''">
          <b-input v-model="model.name" placeholder="לדוגמה: הגשת תרגיל 3 — דיני חוזים" />
        </b-field>
        <b-field label="תיאור">
          <input type="text" style="border: 1px solid gray; width: 100%; padding: 14px;" v-model="model.description"
            name="" id="">
        </b-field>

        <!-- סיווג -->
        <h4 class="section-container">סיווג</h4>
        <div class="row">
          <b-field label="סוג משימה *" :type="submitted && !model.taskTypeId ? 'is-danger' : ''">
            <b-select v-model="model.taskTypeId" placeholder="בחר סוג" expanded @input="onTaskTypeChange">
              <option v-for="t in taskTypes" :key="t.Value" :value="t.Value">{{ t.Text }}</option>
            </b-select>
          </b-field>
          <!-- Only 5 of the 16 task types have sub-types; hide the selector entirely for the rest. -->
          <b-field v-if="subTypes.length" label="תת-סוג">
            <b-select v-model="model.subTypeId" placeholder="בחר תת-סוג" expanded>
              <option :value="null">— ללא —</option>
              <option v-for="s in subTypes" :key="s.Value" :value="s.Value">{{ s.Text }}</option>
            </b-select>
          </b-field>
        </div>
        <div class="row">
          <b-field label="נושא" expanded>
            <FieldInlineSelect label="נושא" placeholder="הקלד לחיפוש נושא" :clearable="true" :filterable="true"
              :options="[]" :fetch-options="onSearchSubjects" v-model="subjectSelection" @onChange="onSubjectChange" />
          </b-field>
          <!-- Read-only: tags are shown as they came from the server, not editable here. -->
          <b-field label="תגיות">
            <div class="tags-readonly">
              <b-tag v-for="t in tagsArray" :key="t" ellipsis>{{ t }}</b-tag>
              <span v-if="!tagsArray.length" class="tags-empty">— ללא תגיות —</span>
            </div>
          </b-field>
        </div>

        <!-- הגדרות -->
        <h4 class="section-container">הגדרות</h4>
        <b-field label="סטטוס">
          <b-switch v-model="model.isActive">
            {{ model.isActive ? "פעיל" : "לא פעיל" }}
          </b-switch>
        </b-field>
        <div class="row">
          <b-field label="משך (דקות)">
            <b-numberinput v-model="model.durationMinutes" :min="0" controls-position="compact" />
          </b-field>
          <!-- Read-only: shown as stored, round-trip unchanged. -->
          <b-field label="עדיפות">
            <span class="value-readonly" :class="{ 'is-empty': !model.priority }">
              {{ model.priority || emptyText }}
            </span>
          </b-field>
          <b-field label="חובה">
            <span class="value-readonly" :class="{ 'is-empty': !model.mustDo }">
              {{ model.mustDo || emptyText }}
            </span>
          </b-field>
        </div>
        <div class="row">
          <b-field label="שלב">
            <span class="value-readonly" :class="{ 'is-empty': isBlank(model.stage) }">
              {{ displayValue(model.stage) }}
            </span>
          </b-field>
          <b-field label="יום בשלב">
            <span class="value-readonly" :class="{ 'is-empty': isBlank(model.stageDay) }">
              {{ displayValue(model.stageDay) }}
            </span>
          </b-field>
          <b-field label="מזהה חיצוני">
            <b-numberinput v-model="model.externalId" :controls="false" placeholder="ExternalId" />
          </b-field>
        </div>
        <b-field label="הערות">
          <b-input type="textarea" v-model="model.taskComments" rows="2" placeholder="הערות פנימיות" />
        </b-field>
      </section>

      <footer class="modal-card-foot">
        <b-button :loading="isLoading" type="is-primary" label="שמור" @click="save" />
        <b-button :disabled="isLoading" label="ביטול" @click="$emit('close')" />
      </footer>
    </div>
  </div>
</template>

<script>
import { ToastProgrammatic as Toast } from "buefy";
import FieldInlineSelect from "@/components/Fields/FieldInlineSelect.vue";
import TasksService from "@/services/TasksService";
import TasksLookupCache from "@/services/TasksLookupCache";

export default {
  name: "DialogAddTask",
  components: { FieldInlineSelect },
  props: ["store", "row", "onSuccess"],
  data() {
    return {
      submitted: false,
      isLoading: false,
      // [{ Value, Text, SubTypes: [{ Value, Text }] }] — server order, don't re-sort
      taskTypes: [],
      emptyText: "— לא הוגדר —",
      subjectSelection: null,   // { Value, Text } from FieldInlineSelect
      // Display-only split of model.tags (comma-joined string); never written back.
      tagsArray: [],
      model: {
        taskTypeId: null,
        subTypeId: null,
        externalId: null,
        subjectId: null,
        subjectName: null,
        name: null,
        description: "",
        durationMinutes: 0,
        isActive: true,
        stage: null,
        stageDay: null,
        priority: null,
        mustDo: "",
        tags: null,
        taskComments: null,
      },
    };
  },
  computed: {
    isEdit() {
      return !!(this.row && this.row.id);
    },
    // Sub-types ride along on the selected parent — no separate request.
    subTypes() {
      const parent = this.taskTypes.find((t) => t.Value === this.model.taskTypeId);
      return parent ? parent.SubTypes : [];
    },
  },
  created() {
    TasksLookupCache.getTaskTypeTree().then((types) => { this.taskTypes = types; });

    if (this.isEdit) {
      const r = this.row;
      this.model = {
        taskTypeId: r.taskTypeId,
        subTypeId: r.subTypeId,
        externalId: r.externalId,
        subjectId: r.subjectId,
        subjectName: r.subjectName,
        name: r.name,
        description: r.description || "",
        durationMinutes: r.durationMinutes || 0,
        isActive: r.isActive,
        stage: r.stage,
        stageDay: r.stageDay,
        priority: r.priority,
        mustDo: r.mustDo || "",
        tags: r.tags,
        taskComments: r.taskComments,
      };
      this.tagsArray = r.tags ? r.tags.split(",").map((t) => t.trim()).filter(Boolean) : [];
      if (r.subjectId) this.subjectSelection = { Value: r.subjectId, Text: r.subjectName };
    }
  },
  methods: {
    // Picking a different parent invalidates any sub-type already chosen.
    onTaskTypeChange() {
      this.model.subTypeId = null;
    },
    onSearchSubjects({ search, loading, setData }) {
      if (search.length > 1) {
        loading(true);
        return TasksLookupCache.getSubjects()
          .then((subjects) => {
            const term = search.toLowerCase();
            setData(subjects.filter((s) => s.Text && s.Text.toLowerCase().includes(term)));
          })
          .finally(() => loading(false));
      }
      return null;
    },
    // Read-only display: 0 is a real value, so don't let `||` swallow it.
    isBlank(v) {
      return v === null || v === undefined || v === "";
    },
    displayValue(v) {
      return this.isBlank(v) ? this.emptyText : v;
    },
    onSubjectChange() {
      this.model.subjectId = this.subjectSelection ? this.subjectSelection.Value : null;
      this.model.subjectName = this.subjectSelection ? this.subjectSelection.Text : null;
    },
    save() {
      this.submitted = true;
      if (!this.model.name || !this.model.taskTypeId) {
        Toast.open({ type: "is-warning", message: "שם וסוג משימה הם שדות חובה", duration: 4000 });
        return;
      }
      this.isLoading = true;
      // model.tags is passed through untouched — the tags field is read-only.
      const dto = { ...this.model }; // keys match CreateTasksBankModel (case-insensitive binding)
      const req = this.isEdit
        ? TasksService.updateTask(this.row.id, dto)
        : TasksService.createTask(dto);

      req
        .then(() => {
          Toast.open({ type: "is-success", message: "הפעולה בוצעה!", duration: 4000 });
          if (this.onSuccess) this.onSuccess();
          this.$emit("saved");
          this.$emit("close");
        })
        .catch(() => {
          Toast.open({
            type: "is-danger",
            message: "לא היה ניתן לבצע את הפעולה, יש לפנות לתמיכה הטכנית.",
            duration: 8000,
          });
        })
        .finally(() => { this.isLoading = false; });
    },
  },
};
</script>

<style scoped lang="scss">
// Cap the card to the viewport and let the body scroll on its own, regardless of how the
// modal is opened. (EntityListLayout's sideViewModal opens without hasModalCard, so Buefy
// doesn't apply its own height cap — without this the tall form overflows with no scroll.)

.modal-card-body {
  overflow-y: auto;
}

// Inherits the title's font; LTR so multi-digit ids don't reorder next to the RTL text.
.task-id {
  direction: ltr;
  display: inline-block;
  margin-inline-start: 8px;
}

.section-container {
  font-size: 18px;
  font-weight: 700;
  color: #00427e;
  margin: 6px 0 10px;
  padding-bottom: 4px;
  border-bottom: 1px solid #e3e9ef;
}

.row {
  display: flex;
  gap: 14px;

  .field {
    flex: 1;
  }
}

.tags-readonly {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
  min-height: 36px; // keep the row aligned with the inputs beside it
}

.tags-empty {
  color: #8c9bab;
  font-size: 13px;
}

.value-readonly {
  display: flex;
  align-items: center;
  min-height: 36px; // match the inputs on the same row
  color: #363636;

  &.is-empty {
    color: #8c9bab;
    font-size: 13px;
  }
}
</style>
